import {getFormatInfo} from "numfmt";
import {EventBus, Logger, State} from "@bainbridge-growth/node-frontend";
import {
  showWarningAndConfirm
} from "../components/DPStaticDialog/DPStaticDialog";
import ModelSettings from "../services/microsoft/ModelSettings";
import Telemetry from "../services/telemetry/Telemetry";
import Toast from "../services/Toast";
import ExcelUtilities from "../utilities/ExcelUtilities";

// without this init, it wont start up..
Logger.start();
window.logger = Logger.logger;

export default class KeyDriver {

  // Constants for strings used throughout the file
  private static readonly KEY_DRIVERS_RESULTS_TAB_NAME = "Key Drivers and Results";
  private static readonly LOG_PREFIX = "[DrivePoint]";
  private static readonly KEY_DRIVER_MARKER = "Key Driver";
  private static readonly KEY_RESULT_MARKER = "Key Result";
  private static readonly KEY_DRIVER_PREFIX = "•⚡ "; // Bullet (U+2022) + High Voltage (U+26A1) + space
  private static readonly KEY_RESULT_PREFIX = "⚡ "; // Just High Voltage (U+26A1) + space
  private static readonly BULLET_POINT = "•"; // The bullet character used in checks
  
  // Format strings
  private static readonly FORMAT_NUMBER_0 = "number_0";
  private static readonly FORMAT_NUMBER_2 = "number_2";
  private static readonly FORMAT_CURRENCY_0 = "currency_0";
  private static readonly FORMAT_CURRENCY_2 = "currency_2";
  private static readonly FORMAT_PERCENT_0 = "percent_0";
  private static readonly FORMAT_PERCENT_2 = "percent_2";
  
  // Dialog URL paths
  private static readonly STATIC_DIALOG_DRIVERS_WARNING = "/static-driverswarning.html";
  private static readonly STATIC_DIALOG_KEY_DRIVER_NOT_DEFINED = "/static-dialogkeydrivernotdefined.html";
  private static readonly STATIC_DIALOG_KEY_DRIVER_OR_RESULT = "/static-dialogkeydriverorresult.html";
  
  // Constants for markers and styling
  private static readonly KEY_ELEMENT_CONFIG = {
    driver: {
      marker: KeyDriver.KEY_DRIVER_MARKER,
      prefix: KeyDriver.KEY_DRIVER_PREFIX,
      bullet: "•", // Bullet point character
      bolt: "⚡ ", // Lightning bolt with space
      color: "#3975D0" // Blue text for driver (keep blue for readability)
    },
    result: {
      marker: KeyDriver.KEY_RESULT_MARKER,
      prefix: KeyDriver.KEY_RESULT_PREFIX,
      bullet: "", // No bullet for results
      bolt: "⚡ ", // Lightning bolt with space
      color: "#acacac" // Grey text for all markers
    },
    empty: {
      color: "#FFFFFF", // white background
      textColor: "#000000" // default text color
    }
  };
  
  // Font settings
  private static readonly FONT_FAMILY = "Calibri (Body)";
  private static readonly FONT_SIZE = 11;


  // Busy notification dialog path
  private static readonly STATIC_DIALOG_BUSY = "/static-busy.html";

  // Busy guard to prevent re-entry while an operation is in progress
  private static _isBusy = false;

  public static get isBusy(): boolean {
    return KeyDriver._isBusy;
  }

  public static set isBusy(value: boolean) {
    KeyDriver._isBusy = value;
  }


  static async start(): Promise<void> {
    State.registerMany("office:ready", KeyDriver.onOfficeReady);
  }

  static onOfficeReady(): void {
    if (State.get("office:ready")) { KeyDriver.registerAddIn(); }
  }

  public static registerAddIn() {
    try {
      if (Office?.actions) {
        Office.actions.associate("enableKeyDriver", KeyDriver.handleEnableKeyElement);
        logger.debug(`${KeyDriver.LOG_PREFIX} Registered enableKeyDriver function in component`);
        
        Office.actions.associate("disableKeyDriver", KeyDriver.handleDisableKeyElement);
        logger.debug(`${KeyDriver.LOG_PREFIX} Registered disableKeyDriver function in component`);

        Office.actions.associate("enableKeyResult", KeyDriver.handleEnableKeyElement);
        logger.debug(`${KeyDriver.LOG_PREFIX} Registered enableKeyResult function in component`);
        
        Office.actions.associate("disableKeyResult", KeyDriver.handleDisableKeyElement);
        logger.debug(`${KeyDriver.LOG_PREFIX} Registered disableKeyResult function in component`);
      }
    } catch (error) {
      logger.error(`${KeyDriver.LOG_PREFIX} Error registering functions in component:`, error);
    }
  }

  // Cache to track IDs across different handler calls
  private static readonly generatedIDsCache = new Set<string>();
  
  // Function to check if an ID exists in the workbook and in our local cache
  private static async isIDUnique(id: string, worksheet: Excel.Worksheet, localCache: Map<string, boolean>): Promise<boolean> {
    logger.debug(`${KeyDriver.LOG_PREFIX} Checking if ID is unique:`, id);
    
    // First check our local cache for this operation
    const normalizedID = id.toLowerCase();
    if (localCache.has(normalizedID)) {
      logger.debug(`${KeyDriver.LOG_PREFIX} ID already exists in local cache:`, id);
      return false;
    }
    
    // Then check the global cache from previous operations
    if (KeyDriver.generatedIDsCache.has(id)) {
      logger.debug(`${KeyDriver.LOG_PREFIX} ID already exists in global cache:`, id);
      return false;
    }
    
    // Finally, check if it exists in the worksheet
    const searchRange = worksheet.getRange("B:B");
    const searchResult = searchRange.findOrNullObject(id, {
      completeMatch: true,
      matchCase: false,
      searchDirection: Excel.SearchDirection.forward
    });
    
    try {
      await searchRange.context.sync();
      if (!searchResult.isNullObject) {
        logger.debug(`${KeyDriver.LOG_PREFIX} ID found in worksheet:`, id);
        return false;
      }
    } catch (error) {
      logger.error(`${KeyDriver.LOG_PREFIX} Error while searching for ID:`, error);
      // If there's an error, assume it's not unique to be safe
      return false;
    }
    
    logger.debug(`${KeyDriver.LOG_PREFIX} ID is unique:`, id);
    return true;
  }
  
  // Generate a unique ID for a given friendly name
  private static async generateUniqueID(friendlyName: string, sheetName: string, worksheet: Excel.Worksheet, localCache: Map<string, boolean>): Promise<string> {
    if (!friendlyName) return "";
    
    // Thoroughly trim the friendlyName to remove all whitespace characters
    const trimmedName = friendlyName.replace(/^\s+|\s+$/g, "");
    if (!trimmedName) return "";
    
    // Replace % with "percent" before removing special characters
    const nameWithPercent = trimmedName.replace(/%/g, "percent");
    logger.debug(`${KeyDriver.LOG_PREFIX} After replacing % with 'percent':`, nameWithPercent);
    
    // Process each part separately to preserve periods
    const cleanSheetName = sheetName.replace(/[^A-Za-z0-9]/g, "").toLowerCase();
    const cleanFriendlyName = nameWithPercent.replace(/[^A-Za-z0-9]/g, "").toLowerCase();
    
    // Create the base ID with double underscores between first two parts, then single underscore
    const baseID = `${cleanSheetName}__${cleanFriendlyName}_custom`;
    logger.debug(`${KeyDriver.LOG_PREFIX} Base ID created:`, baseID);
    
    // Check if the base ID is unique
    if (await KeyDriver.isIDUnique(baseID, worksheet, localCache)) {
      // Add to our caches
      localCache.set(baseID.toLowerCase(), true);
      KeyDriver.generatedIDsCache.add(baseID);
      return baseID;
    }
    
    // If not unique, try with suffixes using underscores instead of periods
    let counter = 1;
    while (true) {
      const candidateID = `${baseID}___${counter}`;
      if (await KeyDriver.isIDUnique(candidateID, worksheet, localCache)) {
        // Add to our caches
        localCache.set(candidateID.toLowerCase(), true);
        KeyDriver.generatedIDsCache.add(candidateID);
        return candidateID;
      }
      counter++;
    }
  }
  
  // Helper function to extract row numbers from a range address
  private static extractRowNumbers(address: string) {
    logger.debug(`${KeyDriver.LOG_PREFIX} Extracting row numbers from address:`, address);
    
    // For full row selections like "Sheet1!1:5"
    const fullRowMatch = address.match(/(?:.*!)?(\d+):(\d+)$/);
    if (fullRowMatch && fullRowMatch[1] && fullRowMatch[2]) {
      const startRow = parseInt(fullRowMatch[1]);
      const endRow = parseInt(fullRowMatch[2]);
      logger.debug(`${KeyDriver.LOG_PREFIX} Full row selection detected. Rows:`, startRow, "to", endRow);
      const rows = [];
      for (let i = startRow; i <= endRow; i++) {
        rows.push(i);
      }
      return rows;
    }
    
    // For selections with specific start and end cells like "Sheet1!A1:C5"
    const rangeMatch = address.match(/(?:.*!)?[A-Z]+(\d+):[A-Z]+(\d+)$/);
    if (rangeMatch && rangeMatch[1] && rangeMatch[2]) {
      const startRow = parseInt(rangeMatch[1]);
      const endRow = parseInt(rangeMatch[2]);
      logger.debug(`${KeyDriver.LOG_PREFIX} Cell range selection detected. Rows:`, startRow, "to", endRow);
      const rows = [];
      for (let i = startRow; i <= endRow; i++) {
        rows.push(i);
      }
      return rows;
    }
    
    // For a single cell selection like "Sheet1!A1"
    const singleCellMatch = address.match(/(?:.*!)?[A-Za-z]+(\d+)$/);
    if (singleCellMatch && singleCellMatch[1]) {
      const row = parseInt(singleCellMatch[1]);
      logger.debug(`${KeyDriver.LOG_PREFIX} Single cell selection detected. Row:`, row);
      return [row];
    }
    
    logger.debug(`${KeyDriver.LOG_PREFIX} Could not extract row numbers from address:`, address);
    return [];
  }
  
  // Helper function to write value to column B with no indentation
  private static async writeToColumnB(cell: Excel.Range, value: string, context: Excel.RequestContext): Promise<void> {
    // Set the cell value
    cell.values = [[value]];
    
    // Ensure no indentation is applied
    cell.format.horizontalAlignment = Excel.HorizontalAlignment.left;
    cell.format.indentLevel = 0;
    
    // Apply standard font and ensure it's never bold
    KeyDriver.ensureColumnBNotBold(cell);
    
    // Make sure we're not carrying over any other formatting that might cause issues
    cell.format.font.italic = false;
    // The bold=false is already set by ensureColumnBNotBold
    logger.debug(`${KeyDriver.LOG_PREFIX} Set column B value with no indentation:`, value);
    await context.sync();
  }
  
  // Keep these as wrapper functions for backward compatibility and clarity of intent
  private static async lockKeyDriversTab(context: Excel.RequestContext): Promise<boolean> {
    return ExcelUtilities.lockWorksheet(context, KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME);
  }
  
  private static async unlockKeyDriversTab(context: Excel.RequestContext): Promise<boolean> {
    return ExcelUtilities.unlockWorksheet(context, KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME);
  }
  
  // Helper function to determine the element type (driver or result) from event name
  private static getElementTypeFromEvent(event: Office.AddinCommands.Event): "driver" | "result" {
    const eventId = event.source?.id?.toLowerCase() || "";
    return eventId.includes("result") ? "result" : "driver";
  }
  
  // Function to convert existing IDs to the new format if needed
  private static convertToNewIDFormat(oldId: string): string {
    // Check if ID already follows the new format (has double underscore)
    if (oldId.includes("__") && oldId.includes("_custom")) {
      return oldId;
    }
    
    // Handle old format: "sheet.name.custom" or "sheet.name.custom.1"
    const parts = oldId.split(".");
    
    if (parts.length < 3) {
      // Can't reliably convert, return as is
      return oldId;
    }
    
    // Extract sheet name and friendly name
    const sheetName = parts[0];
    let friendlyName = parts[1];
    let suffix = "";
    
    // Check if there's a numeric suffix
    if (parts.length > 3) {
      suffix = `_${parts[3]}`;
    }
    
    // Create the new format
    return `${sheetName}__${friendlyName}_custom${suffix}`;
  }
  
  // Load existing IDs from column B into the local cache
  private static async preloadExistingIDs(worksheet: Excel.Worksheet, localCache: Map<string, boolean>): Promise<void> {
    const usedRange = worksheet.getUsedRange();
    usedRange.load("address");
    await usedRange.context.sync();
    
    if (usedRange.address) {
      const match = usedRange.address.match(/([A-Z]+)(\d+):([A-Z]+)(\d+)/);
      if (match) {
        const startRow = parseInt(match[2]);
        const endRow = parseInt(match[4]);
        
        const columnB = worksheet.getRange(`B${startRow}:B${endRow}`);
        columnB.load("values");
        await columnB.context.sync();
        
        const columnBValues = columnB.values;
        if (columnBValues && Array.isArray(columnBValues)) {
          for (let i = 0; i < columnBValues.length; i++) {
            if (columnBValues[i] && columnBValues[i][0]) {
              const value = columnBValues[i][0].toString().trim();
              if (value) {
                // Normalize ID format
                const normalizedID = KeyDriver.convertToNewIDFormat(value).toLowerCase();
                localCache.set(normalizedID, true);
              }
            }
          }
        }
        logger.debug(`${KeyDriver.LOG_PREFIX} Preloaded existing IDs into local cache, count:`, localCache.size);
      }
    }
  }
  
  // Add this function before handleEnableKeyElement
  private static async checkForBulletPoint(context: Excel.RequestContext, rowNumber: number): Promise<boolean> {
    const worksheet = context.workbook.worksheets.getActiveWorksheet();
    const columnACell = worksheet.getRange(`A${rowNumber}`);
    columnACell.load("values");
    await context.sync();
  
    let cellValueA = "";
    if (columnACell.values && columnACell.values[0] && columnACell.values[0][0]) {
      cellValueA = columnACell.values[0][0].toString();
    }
    return cellValueA.includes(KeyDriver.BULLET_POINT);
  }
  
  // Add this new helper function to check if we're on the Key Drivers tab and show the warning if needed
  private static async checkAndHandleKeyDriversResultsTab(event: Office.AddinCommands.Event): Promise<boolean> {
    try {
      // Check if we're on the Key Drivers and Results tab
      const isOnKeyDriversTab = await Excel.run(async (context) => {
        return await KeyDriver.isOnKeyDriversResultsTab(context);
      });
      
      if (isOnKeyDriversTab) {
        // Log a warning
        logger.warn(`${KeyDriver.LOG_PREFIX} Invalid operation on ${KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME} Tab`);
        
        // Show warning dialog to the user
        await showWarningAndConfirm(`${window.location.origin}${KeyDriver.STATIC_DIALOG_DRIVERS_WARNING}?t=${Date.now()}`);
        
        // Complete the event
        event.completed();
        
        // Return true to indicate the caller should exit
        return true;
      }
      
      // Return false to indicate the caller should continue processing
      return false;
    } catch (error) {
      logger.error(`${KeyDriver.LOG_PREFIX} Error checking for ${KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME} tab:`, error);
      // In case of error, assume we're not on the Key Drivers tab (safer to proceed)
      return false;
    }
  }
  
  // Helper function to check if cells have the opposite marker
  private static async checkForOppositeMarker(context: Excel.RequestContext, rowNumbers: number[], elementType: "driver" | "result"): Promise<boolean> {
    const worksheet = context.workbook.worksheets.getActiveWorksheet();
    
    for (const rowNumber of rowNumbers) {
      const columnACell = worksheet.getRange(`A${rowNumber}`);
      columnACell.load("values");
      await context.sync();
      
      let cellValue = "";
      if (columnACell.values && columnACell.values[0] && columnACell.values[0][0]) {
        cellValue = columnACell.values[0][0].toString();
      }
      
      // Check for opposite marker
      if (elementType === "driver") {
        // When adding Driver, check if cell already has Result
        if (cellValue.includes(KeyDriver.KEY_ELEMENT_CONFIG.result.prefix) || 
            cellValue.includes(KeyDriver.KEY_ELEMENT_CONFIG.result.marker)) {
          return true;
        }
      } else {
        // When adding Result, check if cell already has Driver
        if (cellValue.includes(KeyDriver.KEY_ELEMENT_CONFIG.driver.prefix) || 
            cellValue.includes(KeyDriver.KEY_ELEMENT_CONFIG.driver.marker)) {
          return true;
        }
      }
    }
    
    return false;
  }
  
  // Add this helper function to more precisely remove specific markers while preserving bullets
  private static removeMarkerPreservingBullet(text: string, elementType: "driver" | "result"): string {
    if (!text) return text;
    
    if (elementType === "driver") {
      // Remove "⚡ Key Driver" while preserving bullet point if present
      return text.replace(/⚡\s*Key Driver/g, "")
        .replace(/\s*,\s*$/, "") // Remove trailing comma and spaces
        .replace(/^\s*,\s*/, "") // Remove leading comma and spaces
        .trim();
    } else {
      // Remove "⚡ Key Result" or just "Key Result" while preserving other markers like '↑'
      return text.replace(/⚡\s*Key Result/g, "")
        .replace(/\s*Key Result/g, "") // Also remove "Key Result" without the lightning bolt
        .replace(/\s*,\s*$/, "") // Remove trailing comma and spaces
        .replace(/^\s*,\s*/, "") // Remove leading comma and spaces
        .trim();
    }
  }


  // Core disable logic - can be called directly or via handleDisableKeyElement event handler
  public static async disableKeyElement(elementType: "driver" | "result", rowNumbers: number[], worksheetName?: string): Promise<void> {
    logger.debug(`${KeyDriver.LOG_PREFIX} Starting disableKeyElement for ${elementType}`);
    const marker = KeyDriver.KEY_ELEMENT_CONFIG[elementType].marker;
    logger.debug(`${KeyDriver.LOG_PREFIX} Handling disable ${elementType}, marker: ${marker}`);
    const disabledRows: { rowData: string[]; values: { cell: string; value: string; date: string }[] }[] = [];
    try {
      await Excel.run(async (context) => {
        logger.debug(`${KeyDriver.LOG_PREFIX} Inside Excel.run context...`);
        if (rowNumbers.length > 0) {
          const worksheet = worksheetName
            ? context.workbook.worksheets.getItem(worksheetName)
            : context.workbook.worksheets.getActiveWorksheet();
          worksheet.load("name");
          await context.sync();
          const sheetName = worksheet.name;
          
          // Check if Key Drivers tab exists before proceeding
          const keyDriversTabPresent = await KeyDriver.isKeyDriversResultsTabPresent(context);
          
          // If tab exists, unlock it once at the beginning
          let keyDriversTab: Excel.Worksheet | null = null;
          if (keyDriversTabPresent) {
            keyDriversTab = context.workbook.worksheets.getItem(KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME);
            logger.debug(`${KeyDriver.LOG_PREFIX} Unlocking ${KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME} tab once for all operations`);
            await KeyDriver.unlockKeyDriversTab(context);
          } else {
            logger.warn(`${KeyDriver.LOG_PREFIX} No ${KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME} tab present, skipping reference removal`);
          }
  
          // Store IDs to find in Key Drivers tab
          const idsToFind: { id: string, rowNumber: number }[] = [];

          // Get lastUsedColumn ONCE and batch-load A:C + header for all rows
          const disLastUsedCol = await ExcelUtilities.getLastUsedColumn(worksheet);
          const disHeaderRange = worksheet.getRange(`K2:${disLastUsedCol}2`);
          disHeaderRange.load("values");

          const disableRowRanges: {rowNumber: number; abcRange: Excel.Range}[] = [];
          for (const rowNumber of rowNumbers) {
            const abcRange = worksheet.getRange(`A${rowNumber}:C${rowNumber}`);
            abcRange.load("values");
            disableRowRanges.push({rowNumber, abcRange});
          }
          await context.sync();
          const disHeaderDates = disHeaderRange.values[0];
          logger.debug(`${KeyDriver.LOG_PREFIX} Batch-loaded header + A:C for ${disableRowRanges.length} rows in a single sync`);

          // First process all source cells to get their values and update them
          for (let drIdx = 0; drIdx < disableRowRanges.length; drIdx++) {
            const {rowNumber, abcRange} = disableRowRanges[drIdx];
            // Update task pane progress
            EventBus.dispatch({
              type: "keydriver:progress:indicator",
              value: {active: true, current: drIdx + 1, total: disableRowRanges.length, message: `Removing ${elementType === "driver" ? "Key Driver" : "Key Result"} ${drIdx + 1} of ${disableRowRanges.length}...`}
            } as any);
            try {
              logger.debug(`${KeyDriver.LOG_PREFIX} Processing row ${rowNumber}`);

              const rowValues = abcRange.values[0];

              // Get the cell reference for column A (needed for writing later)
              const columnACell = worksheet.getRange(`A${rowNumber}`);

              // Get the current value of the cell
              let cellValue = "";
              if (rowValues[0] !== null && rowValues[0] !== undefined && rowValues[0] !== "") {
                cellValue = rowValues[0].toString();
              }
              logger.debug(`${KeyDriver.LOG_PREFIX} Current value for A${rowNumber}:`, cellValue);
  
              // Get ID from column B
              let id = "";
              if (rowValues[1] !== null && rowValues[1] !== undefined && rowValues[1] !== "") {
                id = rowValues[1].toString();
              }
  
              // Get friendly name from column C
              let friendlyName = "";
              if (rowValues[2] !== null && rowValues[2] !== undefined && rowValues[2] !== "") {
                friendlyName = rowValues[2].toString();
              }
  
              // Instead of checking for markers in comma-separated values,
              // check for our prefixes directly
              const isKeyDriver = elementType === "driver";
              const prefix = isKeyDriver ? KeyDriver.KEY_ELEMENT_CONFIG.driver.prefix : KeyDriver.KEY_ELEMENT_CONFIG.result.prefix;
              const hasPrefix = cellValue.includes(prefix);
              const hasTextMarker = cellValue.includes(isKeyDriver ? "Key Driver" : "Key Result");
              
              if (hasPrefix || hasTextMarker) {
                // Load K-onwards data per-row using cached lastUsedCol (saves getLastUsedColumn sync)
                const cellValues: { cell: string; value: string; date: string }[] = [];
                
                try {
                  const headerDates = disHeaderDates;
                  const kRange = worksheet.getRange(`K${rowNumber}:${disLastUsedCol}${rowNumber}`);
                  kRange.load("values");
                  await context.sync();
                  const sourceValues = kRange.values[0];

                  if (sourceValues && sourceValues.length > 0) {
                    sourceValues.forEach((value, index) => {
                      if (value !== null && value !== undefined && value !== "") {
                        const colIndex = index + 11; // K is the 11th column
                        const colLetter = ExcelUtilities.getColumnLetter(colIndex);
                        
                        cellValues.push({
                          cell: `${colLetter}${rowNumber}`,
                          value: value,
                          date: headerDates[index] || ""
                        });
                      }
                    });
                  }
                  
                  // Create row data array for this disabled row
                  const rowData = [
                    elementType === "driver" ? "Key Driver" : "Key Result", // Column A type
                    id, // Column B - ID
                    friendlyName, // Column C - Friendly Name
                    "", // Column D
                    "", // Column E
                    "", // Column F
                    "", // Column G
                    "", // Column H
                    sheetName, // Column I - Tab name
                    "" // Column J - format string (not critical for disabling)
                  ];
                  
                  // Add to the disabledRows array
                  disabledRows.push({
                    rowData: rowData,
                    values: cellValues
                  });
                  
                  // Store ID for later lookup in Key Drivers tab
                  if (id) {
                    idsToFind.push({id, rowNumber});
                  }
                  
                  logger.debug(`${KeyDriver.LOG_PREFIX} Added row ${rowNumber} to disabledRows array`);
                } catch (valueError) {
                  logger.error(`${KeyDriver.LOG_PREFIX} Error collecting values for row ${rowNumber}:`, valueError);
                }
                
                // Instead of using removeExistingPrefixes and removeMarkerFromText,
                // use the new function that preserves bullets
                const newCellValue = KeyDriver.removeMarkerPreservingBullet(cellValue, elementType);
                logger.debug(`${KeyDriver.LOG_PREFIX} Current value: "${cellValue}", after marker removal: "${newCellValue}"`);

                // Queue the cell update (defer sync to batch all writes)
                columnACell.values = [[newCellValue]];
                
                // Apply standard font and ensure it's never bold
                KeyDriver.ensureColumnANotBold(columnACell);
                
                // Reset formatting - only set text color to black for Key Results
                if (elementType === "result") {
                  columnACell.format.font.color = KeyDriver.KEY_ELEMENT_CONFIG.empty.textColor;
                }
                
                // Reset other formatting for both types
                columnACell.format.fill.color = KeyDriver.KEY_ELEMENT_CONFIG.empty.color;
                columnACell.format.font.italic = false;
                // The bold=false is already set by ensureColumnANotBold
              } else {
                logger.debug(`${KeyDriver.LOG_PREFIX} No prefix or marker found for ${elementType} in row ${rowNumber}, skipping`);
              }
            } catch (rowError) {
              logger.error(`${KeyDriver.LOG_PREFIX} Error processing row ${rowNumber}:`, rowError);
              // Continue with next row even if this one fails
            }
          }

          // Commit any remaining queued source cell writes
          await context.sync();

          // Now process all references in Key Drivers tab if it exists
          if (keyDriversTabPresent && keyDriversTab && idsToFind.length > 0) {
            logger.debug(`${KeyDriver.LOG_PREFIX} Processing ${idsToFind.length} references in Key Drivers tab`);
            
            try {
              // Get the used range
              const usedRange = keyDriversTab.getUsedRange();
              usedRange.load("rowCount");
              await context.sync();
              
              const totalRows = usedRange.rowCount;
              
              // Add more detailed logging
              logger.debug(`${KeyDriver.LOG_PREFIX} Total rows in Key Drivers tab: ${totalRows}`);
              logger.debug(`${KeyDriver.LOG_PREFIX} IDs to find: ${JSON.stringify(idsToFind.map(item => item.id))}`);

              // Phase 1: Scan all rows and collect ALL matching row numbers (including duplicates)
              const idsToFindSet = new Set(idsToFind.map(item => item.id));
              const matchedRows: number[] = [];
              const chunkSize = 50;
              for (let startRow = 1; startRow <= totalRows; startRow += chunkSize) {
                const endRow = Math.min(startRow + chunkSize - 1, totalRows);
                
                // Load a chunk of rows - include column J for format string
                const rangeToCheck = keyDriversTab.getRange(`A${startRow}:J${endRow}`);
                rangeToCheck.load("values");
                await context.sync();
                
                const values = rangeToCheck.values;
                logger.debug(`${KeyDriver.LOG_PREFIX} Checking rows ${startRow} to ${endRow}, found ${values.length} rows`);
                
                // Check each row in the chunk
                for (let i = 0; i < values.length; i++) {
                  const currentRow = startRow + i;
                  const rowValues = values[i];
                  
                  // Skip empty rows or rows without proper values
                  if (!rowValues[0]) continue;

                  logger.debug(`${KeyDriver.LOG_PREFIX} Checking row ${currentRow}: Type=${rowValues[0]}, ID=${rowValues[1]}, Tab=${rowValues[8]}`);

                  const rowId = rowValues[1]?.toString() || "";
                  if (!idsToFindSet.has(rowId)) continue;

                  // Modify the search criteria to match both exact and prefixed values
                  const expectedTypeInA = elementType === "driver" ? "Key Driver" : "Key Result";
                  const expectedPrefixedTypeInA = elementType === "driver"
                    ? `${KeyDriver.KEY_ELEMENT_CONFIG.driver.prefix}${KeyDriver.KEY_ELEMENT_CONFIG.driver.marker}`
                    : `${KeyDriver.KEY_ELEMENT_CONFIG.result.prefix}${KeyDriver.KEY_ELEMENT_CONFIG.result.marker}`;

                  // Check both the exact type and the prefixed type, plus tab name
                  if ((rowValues[0] === expectedTypeInA || rowValues[0] === expectedPrefixedTypeInA ||
                       rowValues[0]?.toString().includes(expectedTypeInA)) &&
                      rowValues[8] === sheetName) {
                    logger.debug(`${KeyDriver.LOG_PREFIX} Found matching reference at row ${currentRow} for ID ${rowId}`);
                    matchedRows.push(currentRow);
                  } else {
                    logger.debug(`${KeyDriver.LOG_PREFIX} Found ID ${rowId} at row ${currentRow}, but other criteria don't match:`);
                    logger.debug(`${KeyDriver.LOG_PREFIX} Type expected: ${expectedTypeInA}, actual: ${rowValues[0]}`);
                    logger.debug(`${KeyDriver.LOG_PREFIX} Tab expected: ${sheetName}, actual: ${rowValues[8]}`);
                  }
                }
              }

              // Phase 2: Delete all matched rows in reverse order (highest row first) to avoid row-shift issues
              logger.debug(`${KeyDriver.LOG_PREFIX} Found ${matchedRows.length} total matching rows (including duplicates) to delete`);

              // Sort descending so we delete from bottom to top
              matchedRows.sort((a, b) => b - a);

              for (const rowToDelete of matchedRows) {
                try {
                  const rowRange = keyDriversTab.getRange(`A${rowToDelete}:J${rowToDelete}`);
                  rowRange.clear(Excel.ClearApplyTo.contents);
                  try {
                    const numericRange = await ExcelUtilities.getRowDataRange(keyDriversTab, rowToDelete);
                    numericRange.clear(Excel.ClearApplyTo.contents);
                  } catch (numericRangeError) {
                    logger.warn(`${KeyDriver.LOG_PREFIX} No numeric data to clear for row ${rowToDelete}:`, numericRangeError);
                  }
                  await context.sync();
                  logger.debug(`${KeyDriver.LOG_PREFIX} Successfully cleared row ${rowToDelete}`);
                  const fullRowRange = keyDriversTab.getRange(`${rowToDelete}:${rowToDelete}`);
                  fullRowRange.delete(Excel.DeleteShiftDirection.up);
                  await context.sync();
                  logger.debug(`${KeyDriver.LOG_PREFIX} Successfully deleted row ${rowToDelete}`);
                } catch (clearError) {
                  logger.error(`${KeyDriver.LOG_PREFIX} Error clearing/deleting row ${rowToDelete}:`, clearError);
                }
              }

              // Commit any remaining queued target cell writes
              await context.sync();

              if (matchedRows.length === 0) {
                logger.warn(`${KeyDriver.LOG_PREFIX} Could not find any matching rows in the Key Drivers tab for IDs: ${JSON.stringify(idsToFind.map(item => item.id))}`);
              } else {
                logger.debug(`${KeyDriver.LOG_PREFIX} Successfully removed ${matchedRows.length} rows from Key Drivers tab`);
              }
            } catch (error) {
              logger.error(`${KeyDriver.LOG_PREFIX} Error removing references from ${KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME} tab:`, error);
            }
            
          }
        }

        // Recalculate to ensure formulas/references are updated after removal
        EventBus.dispatch({
          type: "keydriver:progress:indicator",
          value: {active: true, current: rowNumbers.length, total: rowNumbers.length, message: "Recalculating formulas..."}
        } as any);
        context.workbook.application.calculate(Excel.CalculationType.full);
        await context.sync();
        logger.debug(`${KeyDriver.LOG_PREFIX} Triggered full recalculation after disable`);
      });

      await Excel.run(async (context) => {
        try {
          logger.debug(`${KeyDriver.LOG_PREFIX} Locking ${KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME} tab after all operations`);
          await KeyDriver.lockKeyDriversTab(context);
        } catch (error) {
          logger.error(`${KeyDriver.LOG_PREFIX} Error locking ${KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME} tab after all operations:`, error);
        }
      });
      
      // Now check if we have any disabled rows to process
      logger.debug(`${KeyDriver.LOG_PREFIX} Found ${disabledRows.length} rows to disable`);
      if (disabledRows.length > 0) {
        const settings = await ModelSettings.get();
        // After successful disable, call the store to record the changes
        try {
          // Map the disabled rows to the format expected by the store
          const disabledItems = disabledRows.map(row => {
            const baseItem = {
              planId: settings.sharepointItemId,
              metric: row.rowData[1], // Column B - ID
              metricFriendlyName: row.rowData[2], // Column C - Friendly Name
              json_data: row.values, // Cell values with dates
              tab_name: row.rowData[8] || "", // Column I or empty string
              data_format: row.rowData[9] || "number_0" // Column J or default to number_0
            };
  
            if (elementType === "driver") {
              return {
                ...baseItem,
                key_driver: false
              };
            } else {
              return {
                ...baseItem,
                key_result: false
              };
            }
          });
  
          logger.debug(`${KeyDriver.LOG_PREFIX} Sending ${disabledItems.length} disabled items to store`);
          
          logger.debug(`${KeyDriver.LOG_PREFIX} Successfully updated store with disabled items`);
        } catch (storeError) {
          logger.error(`${KeyDriver.LOG_PREFIX} Error updating store:`, storeError);
          logger.error(`${KeyDriver.LOG_PREFIX} Failed to update store after disabling key element`, storeError);
        }
      } else {
        logger.debug(`${KeyDriver.LOG_PREFIX} No rows were disabled, skipping store update`);
      }
      Telemetry.track("DrivePoint Disable Drivers", {elementType});
    } catch (error) {
      logger.error(`${KeyDriver.LOG_PREFIX} Error disabling element:`, error);
      throw error;
    } finally {
      EventBus.dispatch({type: "keydriver:progress:indicator", value: null} as any);
    }
  }

  // Event handler wrapper for Office menu actions - delegates to disableKeyElement
  private static async handleDisableKeyElement(event: Office.AddinCommands.Event) {
    logger.debug(`${KeyDriver.LOG_PREFIX} Starting handleDisableKeyElement`);
    if (KeyDriver.isBusy) {
      logger.debug(`${KeyDriver.LOG_PREFIX} Operation already in progress, ignoring request`);
      KeyDriver.showBusyNotification();
      event.completed();
      return;
    }
    KeyDriver.isBusy = true;
    try { await Office.addin.showAsTaskpane(); } catch (error) {
      logger.warn(`${KeyDriver.LOG_PREFIX} Error showing task pane:`, error);
    }
    try {
      const elementType = KeyDriver.getElementTypeFromEvent(event);
      if (await KeyDriver.checkAndHandleKeyDriversResultsTab(event)) { return; }
      let rowNumbers: number[] = [];
      await Excel.run(async (context) => {
        const range = context.workbook.getSelectedRange();
        range.load("address");
        await context.sync();
        rowNumbers = KeyDriver.extractRowNumbers(range.address);
      });
      if (rowNumbers.length > 0) {
        await KeyDriver.disableKeyElement(elementType, rowNumbers);
      }
      Toast.success("Key Drivers/Results disabled successfully.");
    } catch (error) {
      logger.error(`${KeyDriver.LOG_PREFIX} Error in handleDisableKeyElement:`, error);
      Toast.error(error instanceof Error ? error : new Error("Failed to disable Key Drivers/Results. Please try again."));
    } finally {
      KeyDriver.isBusy = false;
      event.completed();
    }
  }

  // Core enable logic - can be called directly or via handleEnableKeyElement event handler
  public static async enableKeyElement(elementType: "driver" | "result", rowNumbers: number[], worksheetName?: string): Promise<{ rowData: string[]; values: { date: string, formula: string, numberFormat: string }[] }[]> {
    const insertedRows: {
      rowData: string[];
      values: { date: string, formula: string, numberFormat: string }[]
    }[] = [];
    const actionName = elementType === "driver" ? "Key Driver" : "Key Result";
    KeyDriver.generatedIDsCache.clear();
    logger.debug(`${KeyDriver.LOG_PREFIX} Cleared generated IDs cache for ${actionName}`);
    try {
      await Excel.run(async (context) => {
        const localIDCache = new Map<string, boolean>();
        const worksheet = worksheetName
          ? context.workbook.worksheets.getItem(worksheetName)
          : context.workbook.worksheets.getActiveWorksheet();
        worksheet.load("name");
        await context.sync();
        
        // Store the original tab name at the start to prevent it from being overwritten
        const originalTabName = worksheet.name;
        logger.debug(`${KeyDriver.LOG_PREFIX} Original tab name: ${originalTabName}`);
        
        // Preload existing IDs
        await KeyDriver.preloadExistingIDs(worksheet, localIDCache);

        // Get lastUsedColumn ONCE (avoids repeated syncs inside getHeaderRange/getRowDataRange)
        const lastUsedCol = await ExcelUtilities.getLastUsedColumn(worksheet);
        logger.debug(`${KeyDriver.LOG_PREFIX} Last used column: ${lastUsedCol}`);

        // Load header range ONCE and batch-load A:C for all rows in a single sync
        const sourceHeaderRange = worksheet.getRange(`K2:${lastUsedCol}2`);
        sourceHeaderRange.load("values");

        const rowRanges: {rowNumber: number; abcRange: Excel.Range}[] = [];
        for (const rowNumber of rowNumbers) {
          const abcRange = worksheet.getRange(`A${rowNumber}:C${rowNumber}`);
          abcRange.load("values");
          rowRanges.push({rowNumber, abcRange});
        }
        await context.sync();
        logger.debug(`${KeyDriver.LOG_PREFIX} Batch-loaded header + A:C for ${rowRanges.length} rows in a single sync`);

        // Pre-loaded header dates (reused for all rows)
        const sourceHeaderDates = sourceHeaderRange.values[0];

        // First process all selected rows on the source sheet
        for (let srcIdx = 0; srcIdx < rowRanges.length; srcIdx++) {
          const {rowNumber, abcRange} = rowRanges[srcIdx];
          // Update task pane progress — adding markers to source tab
          EventBus.dispatch({
            type: "keydriver:progress:indicator",
            value: {active: true, current: srcIdx + 1, total: rowRanges.length, message: `Marking ${elementType === "driver" ? "Key Driver" : "Key Result"} ${srcIdx + 1} of ${rowRanges.length}...`}
          } as any);
          logger.debug(`${KeyDriver.LOG_PREFIX} Processing source row:`, rowNumber);

          const rowValues = abcRange.values[0];

          // Get the cells in columns A, B, and C for writing later
          const columnACell = worksheet.getRange(`A${rowNumber}`);
          const columnBCell = worksheet.getRange(`B${rowNumber}`);

          // Get the current value of column A (key marker)
          let cellValueA = "";
          if (rowValues[0] !== null && rowValues[0] !== undefined && rowValues[0] !== "") {
            cellValueA = rowValues[0].toString();
          }
          
          // Get the current value of column B (ID)
          let cellValueB = "";
          let hasBValue = false;
          if (rowValues[1] !== null && rowValues[1] !== undefined && rowValues[1] !== "") {
            cellValueB = rowValues[1].toString();
            hasBValue = cellValueB.trim() !== "";
          }
          
          // Get the current value of column C (friendly name)
          let cellValueC = "";
          if (rowValues[2] !== null && rowValues[2] !== undefined && rowValues[2] !== "") {
            cellValueC = rowValues[2].toString().trim();
          }
            
          // Only process if column C has a value
          if (cellValueC) {
            // Check if cell already has the opposite marker and skip if it does
            const hasDriverMarker = cellValueA.includes(KeyDriver.KEY_ELEMENT_CONFIG.driver.marker);
            const hasResultMarker = cellValueA.includes(KeyDriver.KEY_ELEMENT_CONFIG.result.marker);

            // Skip cells that already have the opposite marker
            if ((elementType === "driver" && hasResultMarker) || (elementType === "result" && hasDriverMarker)) {
              logger.debug(`${KeyDriver.LOG_PREFIX} Skipping row ${rowNumber} - has opposite marker`);
              continue; 
            }

            let newCellValue: string;

            if (elementType === "driver") {
              // If it's already properly marked as a key driver, don't change
              if (hasDriverMarker) {
                newCellValue = cellValueA;
              } else {
                
                if (cellValueA && !hasResultMarker) {
                  const hasBullet = cellValueA.includes(KeyDriver.KEY_ELEMENT_CONFIG.driver.bullet);
                  
                  if (hasBullet) {
                    newCellValue = `${cellValueA.trim()}${KeyDriver.KEY_ELEMENT_CONFIG.driver.bolt}${KeyDriver.KEY_ELEMENT_CONFIG.driver.marker}`;
                  } else {
                    newCellValue = `${cellValueA.trim()}${KeyDriver.KEY_ELEMENT_CONFIG.driver.prefix}${KeyDriver.KEY_ELEMENT_CONFIG.driver.marker}`;
                  }
                } else {
                  newCellValue = `${KeyDriver.KEY_ELEMENT_CONFIG.driver.prefix}${KeyDriver.KEY_ELEMENT_CONFIG.driver.marker}`;
                }
              }
            } else { 
              if (hasResultMarker) {
                newCellValue = cellValueA;
              } else {
                if (cellValueA && !hasDriverMarker) {
                  newCellValue = `${cellValueA.trim()}${KeyDriver.KEY_ELEMENT_CONFIG.result.bolt}${KeyDriver.KEY_ELEMENT_CONFIG.result.marker}`;
                } else {
                  newCellValue = `${KeyDriver.KEY_ELEMENT_CONFIG.result.prefix}${KeyDriver.KEY_ELEMENT_CONFIG.result.marker}`;
                }
              }
            }

            columnACell.values = [[newCellValue]];
            KeyDriver.ensureColumnANotBold(columnACell);
            columnACell.format.font.color = KeyDriver.KEY_ELEMENT_CONFIG[elementType].color;
            columnACell.format.fill.color = KeyDriver.KEY_ELEMENT_CONFIG.empty.color;
            columnACell.format.indentLevel = 0;

            if (!hasBValue) {
              const uniqueID = await KeyDriver.generateUniqueID(cellValueC, originalTabName, worksheet, localIDCache);
              if (uniqueID) {
                columnBCell.values = [[uniqueID]];
                columnBCell.format.horizontalAlignment = Excel.HorizontalAlignment.left;
                columnBCell.format.indentLevel = 0;
                KeyDriver.ensureColumnBNotBold(columnBCell);
                columnBCell.format.font.italic = false;
                logger.debug(`${KeyDriver.LOG_PREFIX} Queued cell B${rowNumber} with generated ID:`, uniqueID);
                hasBValue = true;
                cellValueB = uniqueID;
              }
            }
            
            // Add this row to insertedRows collection with the values that we have
            const cellValues: { date: string, formula: string, numberFormat: string }[] = [];
            try {
              const headerDates = sourceHeaderDates;
              const kRange = worksheet.getRange(`K${rowNumber}:${lastUsedCol}${rowNumber}`);
              kRange.load("values");
              kRange.load("numberFormat");
              await context.sync();
              const sourceValues = kRange.values[0];
              const sourceFormats = kRange.numberFormat[0];

              if (sourceValues && sourceValues.length > 0) {
                sourceValues.forEach((value, index) => {
                  if (value !== null && value !== undefined && value !== "") {
                    const colIndex = index + 11; // K is the 11th column
                    const colLetter = ExcelUtilities.getColumnLetter(colIndex);
                    
                    cellValues.push({
                      formula: `='${originalTabName}'!${colLetter}${rowNumber}`,
                      date: headerDates[index] || "",
                      numberFormat: sourceFormats[index]
                    });
                  }
                });
              }
              
              // Determine format string
              let formatString = "number_0"; // Default
              for (let i = 0; i < sourceValues.length; i++) {
                if (sourceValues[i] !== undefined && sourceValues[i] !== null && sourceValues[i] !== "") {
                  formatString = KeyDriver.getFormatString(sourceFormats[i]);
                  break;
                }
              }
              
              // Create row data
              const rowData = [
                elementType === "driver" 
                  ? `${KeyDriver.KEY_ELEMENT_CONFIG.driver.prefix}${KeyDriver.KEY_ELEMENT_CONFIG.driver.marker}` // Column A with full prefix
                  : `${KeyDriver.KEY_ELEMENT_CONFIG.result.prefix}${KeyDriver.KEY_ELEMENT_CONFIG.result.marker}`, // Column A with full prefix
                cellValueB, // Column B - ID from source
                `=HYPERLINK("#'${originalTabName}'!C${rowNumber}", "${cellValueC}")`, // Column C - Friendly Name from source
                "", // Column D
                "", // Column E
                "", // Column F
                "", // Column G
                "", // Column H
                originalTabName, // Column I - Tab name
                formatString || ""  // Column J - Format string if available
              ];
              
              // Add this row's values and cell references to our collection
              insertedRows.push({
                rowData: rowData,
                values: cellValues
              });
              
            } catch (valueError) {
              logger.error(`${KeyDriver.LOG_PREFIX} Error collecting values for row ${rowNumber}:`, valueError);
            }
          }
        }
        
        // Now check if Key Drivers tab exists before attempting to modify it
        const keyDriversTabPresent = await KeyDriver.isKeyDriversResultsTabPresent(context);
        if (!keyDriversTabPresent) {
          logger.warn(`${KeyDriver.LOG_PREFIX} No ${KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME} tab present, skipping Key Drivers tab update`);
          return;
        }
        
        // Try to unlock the Key Drivers tab BEFORE we start working with it
        const unlockSuccess = await KeyDriver.unlockKeyDriversTab(context);
        if (!unlockSuccess) {
          // If we can't unlock the tab, log a warning but continue with source sheet updates
          logger.warn(`${KeyDriver.LOG_PREFIX} Failed to unlock Key Drivers and Results tab, skipping tab update`);
          return;
        }
        
        // If we're here, the Key Drivers tab exists and is unlocked - get it
        const keyDriversTab = context.workbook.worksheets.getItem(KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME);
        logger.debug(`${KeyDriver.LOG_PREFIX} Found ${KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME} tab (unlocked), proceeding with updates`);
        
        // Find the appropriate header in column C once
        const columnC = keyDriversTab.getRange("C:C");
        let headerSearch = elementType === "driver" ? "Key Drivers List" : "Key Results List";
        
        // Search for the section header
        const headerSearchResult = columnC.findOrNullObject(headerSearch, {
          completeMatch: true,
          matchCase: false,
          searchDirection: Excel.SearchDirection.forward
        });
        
        await context.sync();
        
        if (headerSearchResult.isNullObject) {
          logger.warn(`${KeyDriver.LOG_PREFIX} Could not find "${headerSearch}" section in Key Drivers and Results tab, skipping tab update`);
          // We've already processed the source cells, so we can exit here
          return;
        }
        
        // Found the header - get its row
        headerSearchResult.load("address");
        await context.sync();
        
        // Extract row number from the address
        const headerAddress = headerSearchResult.address;
        const headerRowMatch = headerAddress.match(/C(\d+)/);
        
        if (!headerRowMatch || !headerRowMatch[1]) {
          logger.error(`${KeyDriver.LOG_PREFIX} Cannot parse row number from address ${headerAddress}`);
          return;
        }
        
        const headerRow = parseInt(headerRowMatch[1]);
        logger.debug(`${KeyDriver.LOG_PREFIX} Found "${headerSearch}" at row ${headerRow}`);
        
        // Create a Set to keep track of rows we've already used in this operation
        const usedRows = new Set<number>();
        
        // Now that all source rows are updated, let's add references to Key Drivers and Results tab
        // Start looking for empty cells from the row after the header
        const HEADER_TO_DATA_OFFSET = 2; // Number of rows after section header before data starts
        const startRow = headerRow + HEADER_TO_DATA_OFFSET;
        logger.debug(`${KeyDriver.LOG_PREFIX} Using data start row ${startRow} (header row ${headerRow} + offset ${HEADER_TO_DATA_OFFSET})`);
        const endRow = startRow + 500; // Look at up to 500 rows after the header
        
        // Load KD tab header range ONCE for date column matching
        const kdLastUsedCol = await ExcelUtilities.getLastUsedColumn(keyDriversTab);
        const kdHeaderRange = keyDriversTab.getRange(`K2:${kdLastUsedCol}2`);
        kdHeaderRange.load("values");

        // Batch-load column A for the search range to find empty rows in memory (small: just 1 column)
        const searchRange = keyDriversTab.getRange(`A${startRow}:A${endRow}`);
        searchRange.load("values");
        await context.sync();
        const searchValues = searchRange.values;
        const kdHeaderDates = kdHeaderRange.values[0];
        logger.debug(`${KeyDriver.LOG_PREFIX} Pre-loaded KD header + column A (${searchValues.length} rows) for empty-row search`);

        // ===== Phase 1: Dedup checks — skip existing, collect new rows =====
        let skippedDuplicates = 0;
        const newAssignments: {
          rowData: string[];
          cellValues: {date: string; formula: string; numberFormat: string}[];
        }[] = [];

        for (const insertedRow of insertedRows) {
          const rowData = insertedRow.rowData;
          const cellValues = insertedRow.values;

          if (!rowData[1] || !rowData[2]) { continue; }

          logger.debug(`${KeyDriver.LOG_PREFIX} Checking dedup for ${rowData[2]}`);

          let idExists = false;
          let existingRowNumber = -1;

          const columnB = keyDriversTab.getRange("B:B");
          const idSearchResult = columnB.findOrNullObject(rowData[1], {
            completeMatch: true,
            matchCase: true,
            searchDirection: Excel.SearchDirection.forward
          });

          try {
            await context.sync();
            if (!idSearchResult.isNullObject) {
              idSearchResult.load("address");
              await context.sync();
              const idAddress = idSearchResult.address;
              const idRowMatch = idAddress.match(/B(\d+)/);
              if (idRowMatch && idRowMatch[1]) {
                existingRowNumber = parseInt(idRowMatch[1]);
                const sectionCell = keyDriversTab.getRange(`A${existingRowNumber}`);
                const tabCell = keyDriversTab.getRange(`I${existingRowNumber}`);
                sectionCell.load("values");
                tabCell.load("values");
                await context.sync();
                const expectedType = elementType === "driver" ? "Key Driver" : "Key Result";
                if (sectionCell.values && sectionCell.values[0] && sectionCell.values[0][0]) {
                  const sectionValue = sectionCell.values[0][0].toString();
                  const tabValue = tabCell.values?.[0]?.[0]?.toString() || "";
                  if ((sectionValue === expectedType || sectionValue.includes(expectedType)) &&
                      tabValue === originalTabName) {
                    idExists = true;
                    logger.debug(`${KeyDriver.LOG_PREFIX} Found existing ID at row ${existingRowNumber}`);
                  }
                }
              }
            }
          } catch (searchError) {
            logger.error(`${KeyDriver.LOG_PREFIX} Error searching for existing ID:`, searchError);
          }

          if (idExists) {
            skippedDuplicates++;
            logger.debug(`${KeyDriver.LOG_PREFIX} Skipping duplicate: ${rowData[2]} (ID: ${rowData[1]}) already exists at row ${existingRowNumber}`);
          } else {
            newAssignments.push({rowData, cellValues});
          }
        }

        logger.debug(`${KeyDriver.LOG_PREFIX} Phase 1 complete: ${skippedDuplicates} skipped (duplicates), ${newAssignments.length} new`);

        // ===== Phase 2: Insert exactly N empty rows for new assignments =====
        let insertionRow = -1;
        for (let idx = 0; idx < searchValues.length; idx++) {
          const searchRow = startRow + idx;
          if (usedRows.has(searchRow)) { continue; }
          if (!searchValues[idx][0]) {
            insertionRow = searchRow;
            break;
          }
        }

        const newCount = newAssignments.length;

        const allAssignments: {
          rowData: string[];
          cellValues: {date: string; formula: string; numberFormat: string}[];
          targetRow: number;
        }[] = [];

        if (newCount > 0 && insertionRow > 0) {
          const insertRange = keyDriversTab.getRange(`${insertionRow}:${insertionRow + newCount - 1}`);
          insertRange.insert(Excel.InsertShiftDirection.down);
          await context.sync();
          logger.debug(`${KeyDriver.LOG_PREFIX} Phase 2 complete: inserted ${newCount} rows at row ${insertionRow}`);
          for (let i = 0; i < newCount; i++) {
            allAssignments.push({
              ...newAssignments[i],
              targetRow: insertionRow + i
            });
          }
        } else if (newCount > 0) {
          logger.warn(`${KeyDriver.LOG_PREFIX} Could not find insertion point for ${newCount} new rows`);
        }

        // ===== Phase 3: Write each row one at a time (A-J + K-onwards + sync per row) =====
        const kdColCount = kdHeaderDates.length;

        for (let rowIdx = 0; rowIdx < allAssignments.length; rowIdx++) {
          const assignment = allAssignments[rowIdx];
          EventBus.dispatch({
            type: "keydriver:progress:indicator",
            value: {active: true, current: rowIdx + 1, total: allAssignments.length, message: `Writing to Key Drivers tab: ${rowIdx + 1} of ${allAssignments.length}...`}
          } as any);
          try {
            const rowRange = keyDriversTab.getRange(`A${assignment.targetRow}:J${assignment.targetRow}`);
            rowRange.values = [assignment.rowData.slice(0, 10)];
            KeyDriver.applyStandardFont(rowRange);
            const cellA = keyDriversTab.getRange(`A${assignment.targetRow}`);
            KeyDriver.ensureColumnANotBold(cellA);
            cellA.format.font.color = KeyDriver.KEY_ELEMENT_CONFIG[elementType].color;
            if (assignment.cellValues && assignment.cellValues.length > 0 && assignment.targetRow > 0) {
              const formulaRow: any[] = new Array(kdColCount).fill("");
              const formatRow: string[] = new Array(kdColCount).fill("General");
              let matchCount = 0;
              for (const cellValue of assignment.cellValues) {
                if (cellValue.date) {
                  for (let dateIndex = 0; dateIndex < kdColCount; dateIndex++) {
                    if (kdHeaderDates[dateIndex] &&
                        kdHeaderDates[dateIndex].toString() === cellValue.date.toString()) {
                      formulaRow[dateIndex] = cellValue.formula;
                      formatRow[dateIndex] = cellValue.numberFormat;
                      matchCount++;
                      break;
                    }
                  }
                }
              }
              if (matchCount > 0) {
                const kRowRange = keyDriversTab.getRange(`K${assignment.targetRow}:${kdLastUsedCol}${assignment.targetRow}`);
                kRowRange.values = [formulaRow];
                kRowRange.numberFormat = [formatRow];
                kRowRange.format.font.name = KeyDriver.FONT_FAMILY;
                kRowRange.format.font.size = KeyDriver.FONT_SIZE;
                kRowRange.format.font.bold = false;
              }
            }
            await context.sync();
            logger.debug(`${KeyDriver.LOG_PREFIX} Row ${rowIdx + 1}/${allAssignments.length} written to row ${assignment.targetRow}`);
          } catch (rowError) {
            logger.error(`${KeyDriver.LOG_PREFIX} Error writing row ${assignment.targetRow}:`, rowError);
          }
        }

        logger.debug(`${KeyDriver.LOG_PREFIX} Phase 3 complete: wrote ${allAssignments.length} rows one by one`);

        // ===== Phase 4: Recalculate Key Drivers tab to ensure formulas/references are rendered =====
        EventBus.dispatch({
          type: "keydriver:progress:indicator",
          value: {active: true, current: allAssignments.length, total: allAssignments.length, message: "Recalculating formulas..."}
        } as any);
        context.workbook.application.calculate(Excel.CalculationType.full);
        await context.sync();
        logger.debug(`${KeyDriver.LOG_PREFIX} Phase 4 complete: triggered full recalculation`);

      });
      
      // Track telemetry after operation
      Telemetry.track("DrivePoint Enable Drivers", { 
        rowCount: rowNumbers.length,
        elementType
      });
      
    } catch (error) {
      logger.error(`${KeyDriver.LOG_PREFIX} Error enabling ${actionName}:`, error);
    }

    try {
      await Excel.run(async (context) => {
        logger.debug(`${KeyDriver.LOG_PREFIX} Locking Key Drivers tab after all operations complete`);
        await KeyDriver.lockKeyDriversTab(context);
        await context.sync();
      });
    } catch (lockError) {
      logger.error(`${KeyDriver.LOG_PREFIX} Error locking Key Drivers tab:`, lockError);
    }
    return insertedRows;
  }

  // Create a new function to specifically check if the Key Drivers and Results tab exists
  private static async isKeyDriversResultsTabPresent(context: Excel.RequestContext): Promise<boolean> {
    return ExcelUtilities.isWorksheetPresent(context, KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME);
  }
  
  // Function to check if we are currently ON the Key Drivers and Results tab
  private static async isOnKeyDriversResultsTab(context: Excel.RequestContext): Promise<boolean> {
    return ExcelUtilities.isOnWorksheetWithName(context, KeyDriver.KEY_DRIVERS_RESULTS_TAB_NAME);
  }
  
  private static getFormatString(numberFormat: string): string {
    logger.debug(`${KeyDriver.LOG_PREFIX} Analyzing number format:`, numberFormat);
    const info = getFormatInfo(numberFormat);
    const hasDecimals = info.maxDecimals > 0;
    
    if (info.type === "percent") {
      return hasDecimals ? KeyDriver.FORMAT_PERCENT_2 : KeyDriver.FORMAT_PERCENT_0;
    }
    if (info.type === "currency") {
      return hasDecimals ? KeyDriver.FORMAT_CURRENCY_2 : KeyDriver.FORMAT_CURRENCY_0;
    }
    return hasDecimals ? KeyDriver.FORMAT_NUMBER_2 : KeyDriver.FORMAT_NUMBER_0;
  }
  
  private static async handleEnableKeyElement(event: Office.AddinCommands.Event) {
    logger.debug(`${KeyDriver.LOG_PREFIX} Starting handleEnableKeyElement`);

    // Busy guard: prevent re-entry while operation is in progress
    if (KeyDriver.isBusy) {
      logger.debug(`${KeyDriver.LOG_PREFIX} Operation already in progress, ignoring request`);
      KeyDriver.showBusyNotification();
      event.completed();
      return;
    }
    KeyDriver.isBusy = true;
    // Open the task pane so progress is visible
    try { await Office.addin.showAsTaskpane(); } catch (error) {
      logger.warn(`${KeyDriver.LOG_PREFIX} Error showing task pane:`, error);
    }

    try {
      // Check if we're on the Key Drivers and Results tab
      if (await KeyDriver.checkAndHandleKeyDriversResultsTab(event)) {
        return;
      }
        
      await Excel.run(async (context) => {
        logger.debug(`${KeyDriver.LOG_PREFIX} Inside Excel.run`);
        const range = context.workbook.getSelectedRange();
        range.load("address");
        await context.sync();
        
        const address = range.address;
        const rowNumbers = KeyDriver.extractRowNumbers(address);
  
        if (rowNumbers.length > 0) {
          const elementType = KeyDriver.getElementTypeFromEvent(event);
          
          // Check if any of the selected cells already have the opposite marker
          const hasOppositeMarker = await KeyDriver.checkForOppositeMarker(context, rowNumbers, elementType);
          if (hasOppositeMarker) {
            // Show warning dialog for cells that have opposite marker
            const dialogUrl = `${window.location.origin}${KeyDriver.STATIC_DIALOG_KEY_DRIVER_OR_RESULT}?t=${Date.now()}`;
            const shouldProceed = await showWarningAndConfirm(dialogUrl);
            if (!shouldProceed) {
              logger.debug(`${KeyDriver.LOG_PREFIX} User cancelled after opposite marker warning`);
              return;
            }
          } else if (await KeyDriver.needsWarning(context, rowNumbers) && elementType === "driver") {
            // Original warning for bullet points
            const dialogUrl = `${window.location.origin}${KeyDriver.STATIC_DIALOG_KEY_DRIVER_NOT_DEFINED}?t=${Date.now()}`;
            const shouldProceed = await showWarningAndConfirm(dialogUrl);
            if (!shouldProceed) {
              logger.debug(`${KeyDriver.LOG_PREFIX} User cancelled`);
              return;
            }
          }

          await KeyDriver.enableKeyElement(elementType, rowNumbers);
        }
      });
      Toast.success("Key Drivers/Results enabled successfully.");
    } catch (error) {
      logger.error(`${KeyDriver.LOG_PREFIX} Error in handleEnableKeyElement:`, error);
      Toast.error(error instanceof Error ? error : new Error("Failed to enable Key Drivers/Results. Please try again."));
    } finally {
      EventBus.dispatch({type: "keydriver:progress:indicator", value: null} as any);
      KeyDriver.isBusy = false;
      event.completed();
    }
  }
  
  // Helper function to check if warning is needed
  private static async needsWarning(context: Excel.RequestContext, rowNumbers: number[]): Promise<boolean> {
    for (const rowNumber of rowNumbers) {
      const hasBullet = await KeyDriver.checkForBulletPoint(context, rowNumber);
      if (!hasBullet) {
        return true;
      }
    }
    return false;
  }

  // Show a brief "operation in progress" dialog that auto-closes after 2 seconds
  private static showBusyNotification(): void {
    try {
      const url = `${window.location.origin}${KeyDriver.STATIC_DIALOG_BUSY}?t=${Date.now()}`;
      Office.context.ui.displayDialogAsync(url, {height: 20, width: 30, displayInIframe: true}, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const dialog = result.value;
          dialog.addEventHandler(Office.EventType.DialogMessageReceived, () => {
            try { dialog.close(); } catch (_e) { /* already closed */ }
          });
          dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
            // no-op, dialog already closed
          });
        }
      });
    } catch (err) {
      logger.debug(`${KeyDriver.LOG_PREFIX} Could not show busy notification: ${err}`);
    }
  }

  // Helper function to apply standard font to any range
  private static applyStandardFont(range: Excel.Range): void {
    // Apply consistent font settings
    range.format.font.name = KeyDriver.FONT_FAMILY;
    range.format.font.size = KeyDriver.FONT_SIZE;
    range.format.font.bold = false; // Ensure normal font weight by default
    range.format.indentLevel = 0; // Remove all indentation
    logger.debug(`${KeyDriver.LOG_PREFIX} Applied standard font (${KeyDriver.FONT_FAMILY} ${KeyDriver.FONT_SIZE}pt) to range`);
  }
  
  // Helper function to ensure column A cells always have normal font weight
  private static ensureColumnANotBold(cell: Excel.Range): void {
    // Apply standard font first (which includes setting font.bold = false)
    KeyDriver.applyStandardFont(cell);
    // Explicitly set bold to false to ensure it's never bold
    cell.format.font.bold = false;
    logger.debug(`${KeyDriver.LOG_PREFIX} Ensured column A cell is not bold`);
  }
  
  // Helper function to ensure column B cells always have normal font weight
  private static ensureColumnBNotBold(cell: Excel.Range): void {
    // Apply standard font first (which includes setting font.bold = false)
    KeyDriver.applyStandardFont(cell);
    // Explicitly set bold to false to ensure it's never bold
    cell.format.font.bold = false;
    logger.debug(`${KeyDriver.LOG_PREFIX} Ensured column B cell is not bold`);
  }
  
}
